import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import axios from "axios";

const ALLOWED_ROLES = ["1", "2", "6", "8"];

const getStoredToken = () => {
  try {
    return JSON.parse(localStorage.getItem("token") || "{}");
  } catch (error) {
    return {};
  }
};

const getTokenHeaders = () => {
  const token = getStoredToken();
  const idToken = token?.idToken || "";

  return {
    Authorization: idToken ? `Bearer ${idToken}` : "",
    "x-access-token": idToken,
    Accept: "application/json",
  };
};

const getProjectCode = (project) =>
  project?.codigo || project?.codigoProyecto || project?.codigo_proyecto || project?.codigo_proyecto_visual || "";

const getProjectName = (project) => project?.nombre || project?.nombre_proyecto || project?.label || "Proyecto";

const getProjectId = (project) => project?.idproyectos || project?.idProyecto || project?.id_proyecto || project?.id || "";

const getConvocatoriaProjectId = (convocatoria) =>
  convocatoria?.idProyecto ||
  convocatoria?.id_proyecto ||
  convocatoria?.idproyecto ||
  convocatoria?.idproyectos ||
  convocatoria?.codigoProyecto ||
  convocatoria?.codigo_proyecto ||
  "";

const getConvocatoriaLabel = (convocatoria) => {
  const actividad = Array.isArray(convocatoria?.actividad)
    ? convocatoria.actividad.join(", ")
    : String(convocatoria?.actividad || convocatoria?.descripcion || "Convocatoria");

  return `${actividad} - ${convocatoria?.fecha || "Sin fecha"} - ${convocatoria?.comunidad || "Sin comunidad"}`;
};

const formatValue = (value, fallback = "Sin dato") => value || fallback;

const normalizeText = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();

const ControlAsistenciaVoluntariado = () => {
  const theme = useTheme();
  const nav = useNavigate();
  const token = getStoredToken();
  const rol = String(token?.rol || "");
  const canAccess = ALLOWED_ROLES.includes(rol);

  const [loading, setLoading] = useState(canAccess);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [convocatorias, setConvocatorias] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedConvocatoriaId, setSelectedConvocatoriaId] = useState("");

  useEffect(() => {
    if (!canAccess) return;

    const loadData = async () => {
      try {
        setLoading(true);
        setError("");

        const [projectsResponse, convocatoriasResponse] = await Promise.all([
          axios.get("/proyectos/permitidos-crear-convocatoria", {
            headers: getTokenHeaders(),
          }),
          axios.get("/convocatoria/", {
            params: { id: 1 },
          }),
        ]);

        setProjects(Array.isArray(projectsResponse.data?.proyectos) ? projectsResponse.data.proyectos : []);
        setConvocatorias(Array.isArray(convocatoriasResponse.data) ? convocatoriasResponse.data : []);
      } catch (err) {
        const status = err?.response?.status;

        if (status === 401) {
          setError("Sesión expirada. Inicia sesión nuevamente.");
        } else if (status === 403) {
          setError("No tienes permiso para acceder al control de asistencias.");
        } else {
          setError("No se pudo cargar la información para el control de asistencias.");
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [canAccess]);

  const selectedProject = useMemo(
    () => projects.find((project) => String(getProjectId(project)) === String(selectedProjectId)),
    [projects, selectedProjectId]
  );

  const filteredConvocatorias = useMemo(() => {
    if (!selectedProjectId) return [];

    return convocatorias.filter((convocatoria) => {
      const projectId = getConvocatoriaProjectId(convocatoria);
      if (projectId) return String(projectId) === String(selectedProjectId);

      const selectedProjectName = normalizeText(getProjectName(selectedProject));
      const selectedProjectCode = normalizeText(getProjectCode(selectedProject));
      const convocatoriaProjectName = normalizeText(convocatoria?.nombre || convocatoria?.nombreProyecto);
      const convocatoriaProjectCode = normalizeText(convocatoria?.codigo || convocatoria?.codigoProyecto);

      return (
        (selectedProjectName && selectedProjectName === convocatoriaProjectName) ||
        (selectedProjectCode && selectedProjectCode === convocatoriaProjectCode)
      );
    });
  }, [convocatorias, selectedProject, selectedProjectId]);

  const selectedConvocatoria = useMemo(
    () => filteredConvocatorias.find((convocatoria) => String(convocatoria.id) === String(selectedConvocatoriaId)),
    [filteredConvocatorias, selectedConvocatoriaId]
  );

  const handleProjectChange = (event) => {
    setSelectedProjectId(event.target.value);
    setSelectedConvocatoriaId("");
  };

  const handleOpenAsistencia = () => {
    if (!selectedConvocatoria) return;

    const serializedConvocatoria = encodeURIComponent(JSON.stringify(selectedConvocatoria));
    nav(`/asistencia?con=${serializedConvocatoria}&origen=voluntariado`);
  };

  if (!canAccess) {
    return (
      <Box m="1.5rem 2.5rem">
        <Alert severity="warning">No tienes permiso para acceder al control de asistencias.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Container component="main" maxWidth="xs">
        <Backdrop sx={{ color: "#fff", zIndex: (themeValue) => themeValue.zIndex.drawer + 1 }} open>
          <CircularProgress color="inherit" />
        </Backdrop>
      </Container>
    );
  }

  return (
    <Box m="1.5rem 2.5rem">
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          underline="hover"
          sx={{ display: "flex", alignItems: "center" }}
          color="inherit"
          href="/inicio"
          onClick={(event) => {
            event.preventDefault();
            nav("/inicio");
          }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Inicio
        </Link>
        <Link
          underline="hover"
          sx={{ display: "flex", alignItems: "center" }}
          color="inherit"
          href="/voluntariado"
          onClick={(event) => {
            event.preventDefault();
            nav("/voluntariado");
          }}
        >
          <VolunteerActivismIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Voluntariado
        </Link>
        <Typography sx={{ display: "flex", alignItems: "center" }} color="text.primary">
          <RuleOutlinedIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Control de asistencias
        </Typography>
      </Breadcrumbs>

      <Typography variant="h3" sx={{ fontWeight: "bold", mt: 2, mb: 3 }}>
        Control de asistencias
      </Typography>

      <Card sx={{ borderRadius: 6, boxShadow: 5, bgcolor: theme.palette.background.alt }}>
        <CardContent>
          <Stack spacing={2.5}>
            <Typography variant="body1" color="text.secondary">
              Gestiona la asistencia de voluntarios en convocatorias de emergencia.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            {!error && projects.length === 0 && (
              <Alert severity="info">No tienes proyectos disponibles para controlar asistencia.</Alert>
            )}

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Proyecto"
                  value={selectedProjectId}
                  onChange={handleProjectChange}
                  disabled={projects.length === 0}
                >
                  <MenuItem value="">
                    <em>Seleccione un proyecto</em>
                  </MenuItem>
                  {projects.map((project) => {
                    const projectId = getProjectId(project);
                    const projectCode = getProjectCode(project);
                    const projectName = getProjectName(project);

                    return (
                      <MenuItem key={projectId} value={projectId}>
                        {projectCode ? `${projectName} - ${projectCode}` : projectName}
                      </MenuItem>
                    );
                  })}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Convocatoria"
                  value={selectedConvocatoriaId}
                  onChange={(event) => setSelectedConvocatoriaId(event.target.value)}
                  disabled={!selectedProjectId || filteredConvocatorias.length === 0}
                >
                  <MenuItem value="">
                    <em>Seleccione una convocatoria</em>
                  </MenuItem>
                  {filteredConvocatorias.map((convocatoria) => (
                    <MenuItem key={convocatoria.id} value={convocatoria.id}>
                      {getConvocatoriaLabel(convocatoria)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {selectedProjectId && filteredConvocatorias.length === 0 && (
              <Alert severity="info">No hay convocatorias activas para el proyecto seleccionado.</Alert>
            )}

            {selectedConvocatoria && (
              <Box
                sx={{
                  border: "1px solid rgba(15, 23, 42, 0.08)",
                  borderRadius: 4,
                  p: 2,
                  bgcolor: theme.palette.background.light || "transparent",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  Datos de la convocatoria
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      <b>Fecha:</b> {formatValue(selectedConvocatoria.fecha)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      <b>Comunidad:</b> {formatValue(selectedConvocatoria.comunidad)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      <b>Descripción:</b>{" "}
                      {formatValue(selectedConvocatoria.descripcion || selectedConvocatoria.descripcionConvo)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography>
                      <b>Punto de encuentro:</b> {formatValue(selectedConvocatoria.puntoEncuentro)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Button variant="outlined" onClick={() => nav("/voluntariado")}>
                Volver
              </Button>
              <Button
                variant="contained"
                sx={{ bgcolor: "teal", color: "white" }}
                disabled={!selectedConvocatoria}
                onClick={handleOpenAsistencia}
              >
                Abrir control de asistencia
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ControlAsistenciaVoluntariado;
